<template>
  <div class="product-detail-page">
    <b-container class="py-5">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="success" label="Loading..."></b-spinner>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-5">
        <h3 class="text-danger">Error loading product</h3>
        <p>{{ error }}</p>
        <b-button variant="outline-primary" to="/">Go Home</b-button>
      </div>

      <!-- Product Content -->
      <div v-else-if="product" class="product-content">
        <!-- Breadcrumb -->
        <div class="mb-4">
          <router-link to="/" class="text-muted text-decoration-none">
            <b-icon-arrow-left></b-icon-arrow-left> Back to Home
          </router-link>
          <span class="mx-2 text-muted">/</span>
          <span class="text-muted text-uppercase">{{ category }}</span>
        </div>

        <b-row>
          <!-- Left Column: Image -->
          <b-col md="6" class="mb-4">
            <div class="image-container shadow-sm rounded bg-white p-4 d-flex align-items-center justify-content-center">
              <b-img 
                :src="getProxiedImage(product.imagen)" 
                fluid 
                :alt="product.titulo"
                @error="handleImageError"
                class="product-detail-image"
              ></b-img>
            </div>
          </b-col>

          <!-- Right Column: Details -->
          <b-col md="6">
            <div class="pl-md-4">
              <div class="d-flex justify-content-between align-items-start mb-2">
                 <h1 class="h2 font-weight-bold text-dark mb-3">{{ product.titulo }}</h1>
              </div>

              <div class="d-flex align-items-center mb-4">
                 <h2 class="text-success font-weight-bold mr-3 mb-0">{{ product.precio }}</h2>
                 <div v-if="product.ai_rating" class="d-flex align-items-center">
                   <b-icon-star-fill variant="warning" v-for="n in 5" :key="n" :scale="n <= Math.round(product.ai_rating) ? 1 : 0.8" :class="n <= Math.round(product.ai_rating) ? '' : 'text-muted-light'"></b-icon-star-fill>
                   <span class="ml-2 text-muted">({{ product.ai_rating }}/5)</span>
                 </div>
              </div>

              <b-card bg-variant="light" border-variant="light" class="mb-4">
                <h5 class="font-weight-bold mb-3">
                  <b-icon-heart-fill variant="danger" class="mr-2"></b-icon-heart-fill> Why We Love It
                </h5>
                <p class="card-text text-dark" style="line-height: 1.6;">
                  {{ product.ai_why_love || product.ai_one_liner || "No specific review available." }}
                </p>
              </b-card>

              <div v-if="product.ai_best_for" class="mb-4">
                <h6 class="text-uppercase text-muted font-weight-bold space-1">Best For</h6>
                <p class="font-weight-medium">{{ product.ai_best_for }}</p>
              </div>

              <!-- Action Buttons -->
              <div class="mt-5">
                <b-button 
                  variant="success" 
                  size="lg" 
                  block 
                  class="py-3 font-weight-bold shadow-sm"
                  @click="openDeal(product)"
                >
                  View Deal <b-icon-box-arrow-up-right class="ml-2"></b-icon-box-arrow-up-right>
                </b-button>
                <p class="text-center mt-2 text-muted small">
                  <b-icon-info-circle class="mr-1"></b-icon-info-circle>
                  Affiliate link. Amazon purchases do not earn CashBoomerang user cash back.
                </p>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-container>
  </div>
</template>

<script>
import { db } from '../main'
import { buildAffiliateUrl } from '../services/affiliateLinks'
import { trackAffiliateClick } from '../services/analytics'
import { applyProductSeo } from '../services/seo'

export default {
  name: 'ProductDetail',
  data() {
    return {
      product: null,
      loading: true,
      error: null
    }
  },
  computed: {
    category() {
      return this.$route.params.category
    },
    id() {
      return this.$route.params.id
    }
  },
  async created() {
    await this.fetchProduct();
  },
  methods: {
    async fetchProduct() {
      this.loading = true;
      this.error = null;
      try {
        const doc = await db.collection(this.category).doc(this.id).get();
        if (doc.exists) {
            this.product = { id: doc.id, ...doc.data() };
            applyProductSeo(this.product, this.category);
        } else {
            this.error = "Product not found.";
        }
      } catch (e) {
        console.error("Error fetching product:", e);
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    getProxiedImage(imageUrl) {
      if (!imageUrl) return '';
      return `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
    },
    handleImageError(event) {
      event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
    },
    openDeal(product) {
      const targetUrl = buildAffiliateUrl(product.link);
      trackAffiliateClick({ ...product, category: this.category }, targetUrl, 'product_detail');
      const outboundWindow = window.open(targetUrl, '_blank');
      if (outboundWindow) outboundWindow.opener = null;
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  min-height: 80vh;
  background-color: #fff;
  padding-top: 40px; 
}
.image-container {
  min-height: 400px;
}
.product-detail-image {
  max-height: 400px;
  object-fit: contain;
}
.text-muted-light {
    color: #e0e0e0;
}
.space-1 {
    letter-spacing: 1px;
}
</style>
